import moment from "moment";
import paginationComponent from "../../components/pagination/pagination.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import { formats as FORMATS } from '../../config/constants';
import languageModel from "../../models/language.model";
import ModelMappingModel from "../../models/model-mapping/model-mapping.model";
import IMEIBasedSuccessRateModel from "../../models/reports/imei-based-success-rate.model";
import ViewBase from "../view-base.view";
import ModelMappingAddAndEditDialog from "./model-mapping-add-and-edit.dialog";
import ModelMappingExcelUploadDialog from "./model-mapping-excel-upload.dialog";

/**
 * Model Mapping View
 * 
 * A page that displays a list of model mapping
 * 
 * @author cmc.pbhieu
 */
export default function ModelMappingView() {
  ViewBase.call(this);

  const DESCENDING_TEXT = 'DESC';
  const ASCENDING_TEXT = 'ASC';

  const MANAGE_MODEL_MAPPING_ACTIONS = {
    insert: "INSERT",
    update: "UPDATE",
    delete: "DELETE",
  };

  const DEFAULT_ITEM = {
    action: MANAGE_MODEL_MAPPING_ACTIONS.insert,
    customerCode: '',
    mapKey: '',
    mapType: 'MODEL_MAP',
    mapAttribute1: '',
    mapAttribute2: '',
  };

  const DEFAULT_FILTERS = {
    customerCode: '',
    mapKey: '',
    mapAttribute1: '',
    mapAttribute2: '',
    orderBy: '',
    sortBy: '',
  }

  const DEFAULT_PAGINATION = {
    pageSize: 10,
    pageIndex: 1,
    totalItems: 0,
  };

  let filters = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);

  let modelMappings = [];
  let customers = [];
  let isExcelUploadOpen = false;
  let isModalManageOpen = false;

  let selectedModelMapping = null;
  let manageAction = '';

  const clearFilter = () => {
    filters = structuredClone(DEFAULT_FILTERS);
    pagination = structuredClone(DEFAULT_PAGINATION);
    searchModelMapping();
  };

  const downloadExcel = () => {
    this.showLoading();
    ModelMappingModel.downloadModelMappingExcel(toQueryParams(filters))
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  const toQueryParams = (param) => {
    return Object.assign({
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex,
      customerCode: param.customerCode ?? '',
      mapKey: param.mapKey ?? '',
      mapAttribute1: param.mapAttribute1 ?? '',
      mapAttribute2: param.mapAttribute2 ?? '',
      orderBy: param.orderBy,
      sortBy: param.sortBy,
    });
  };

  const formatData = (data) => {
    const result = structuredClone(data);

    result.forEach((e) => {
      e.updateDate = e.updateDate ? moment(e.updateDate).format(FORMATS.LOCAL_TIME) : null;
      e.createDate = e.createDate ? moment(e.createDate).format(FORMATS.LOCAL_TIME) : null;
    });

    return result;
  };

  const searchModelMapping = () => {
    this.showLoading();
    ModelMappingModel.search(toQueryParams(filters)).then(result => {
      modelMappings = [];

      if (result != null) {
        modelMappings = formatData(result.data);
        pagination.pageIndex = result.pageIndex;
        pagination.pageSize = result.pageSize;
        pagination.totalItems = result.total;
      }
    })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const getCustomers = () => {
    return IMEIBasedSuccessRateModel.getCustomers()
      .then(result => {
        customers = result;
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const onDeleteAction = (modelMapping) => {
    this.showConfirm(
      this.getText("EM00000265", "Are you sure you want to delete?"),
      this.getText("LT00000373", "Remove Selected"),
      e => {
        if (e == "YES") {
          ModelMappingModel.manage(modelMapping, MANAGE_MODEL_MAPPING_ACTIONS.delete)
            .then((result) => {
              this.showMessage(
                this.getText(result[0].code, result[0].message),
                result[0].transStatus,
                searchModelMapping);
            })
            .catch((error) => {
              this.handleError(error);
            });
        }
      }
    )
  }

  const onUpdateAction = (data) => {
    selectedModelMapping = structuredClone(data);
    isModalManageOpen = true;
    manageAction = MANAGE_MODEL_MAPPING_ACTIONS.update;
  }

  const onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    searchModelMapping();
  };

  const onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    searchModelMapping();
  };

  const onSortClick = (sortBy) => {
    if (!modelMappings.length) {
      return
    }
    if (filters.sortBy === sortBy) {
      filters.orderBy = flipOrderText(filters.orderBy);
    } else {
      filters.sortBy = sortBy;
      filters.orderBy = DESCENDING_TEXT;
    }

    searchModelMapping();
  };

  const flipOrderText = (orderText) => {
    return orderText.toUpperCase() === DESCENDING_TEXT ? ASCENDING_TEXT : DESCENDING_TEXT;
  }

  const SortIconComponent = () => {
    return {
      view: ({ attrs: { column } }) => {
        return filters.sortBy === column
          && (filters.orderBy === DESCENDING_TEXT
            ? m('i.bi.bi-sort-down', { "style": "float:left" })
            : m('i.bi.bi-sort-up', { "style": "float:left" }));
      }
    }
  }

  return {
    oninit: () => {
      getCustomers();
      searchModelMapping();
    },
    view: () => {
      return [
        m(".d-flex.justify-content-end.align-items-end.mb-3",
          m("button.btn.btn-outline-primary.btn-add",
            {
              onclick: () => {
                isModalManageOpen = true;
                manageAction = MANAGE_MODEL_MAPPING_ACTIONS.insert;
              },
            },
            [m("i.bi.bi-plus-lg.me-0")]
          )
        ),

        ///* SEARCH */
        m(".d-flex.flex-column.align-items-end.gap-2.mb-3",
          m(".card.collapse.show#search-section.w-100",
            m(".card-body",
              m(".row.g-4",
                m(".col-md-3.col-lg-3.position-relative.gap-2",
                  m('label.form-label[for="customerSelect"]', this.getText("LT00008046", "Customer")),
                  m(SingleSelectComponent, {
                    id: "customerSelect",
                    items: customers,
                    selectedItem: filters.customerCode,
                    checkmark: true,
                    filterable: true,
                    required: true,
                    keyOnly: true,
                    onSelect: (item) => {
                      filters.customerCode = item;
                    },
                  })
                ),

                m('.col-md-3.col-lg-3.position-relative.gap-2',
                  m('label.form-label', this.getText('LT00001014', 'I/F Model')),
                  m('input.form-control.form-control-bg', {
                    value: filters.mapKey,
                    onchange: (e) => { filters.mapKey = e.target.value.trim(); }
                  })
                ),

                m('.col-md-3.col-lg-3.position-relative.gap-2',
                  m('label.form-label', this.getText('LT00001013', 'Samsung Model')),
                  m('input.form-control.form-control-bg', {
                    value: filters.mapAttribute1,
                    onchange: (e) => { filters.mapAttribute1 = e.target.value.trim(); }
                  })
                ),

                m(".col-12.d-flex.justify-content-end.gap-2",
                  m("button.btn.btn-primary",
                    {
                      onclick: () => {
                        pagination = structuredClone(DEFAULT_PAGINATION);
                        searchModelMapping();
                      },
                    },
                    this.getText("LT00000001", "Search")
                  ),
                  m("button.btn.btn-white",
                    {
                      onclick: clearFilter,
                    },
                    this.getText("LT00000031", "Clear")
                  )
                )
              )
            )
          )
        ),

        ///* TABLE */
        m(".card",
          m(".card-body",
            m(".row.g-2",
              m(".col-12.d-flex.justify-content-end",
                m(".d-flex.gap-2",
                  m("button.btn.btn-primary",
                    {
                      onclick: () => {
                        isExcelUploadOpen = true;
                      },
                    },
                    m("i.bi.bi-file-earmark-arrow-up-fill"),
                    this.getText("LT00000197", "Excel Upload")
                  ),
                  m("button.btn.btn-primary",
                    { onclick: downloadExcel },
                    m("i.bi.bi-file-earmark-arrow-down-fill"),
                    this.getText("LT00000018", "Excel Download")
                  )
                )
              ),
              m(".col-12",
                m(".table-responsive",
                  m("table.table.table-hover.sticky-column-start",
                    m("thead",
                      m("tr.align-middle",
                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('subsidiaryCode'); } },
                          m(SortIconComponent, { column: 'subsidiaryCode' }),
                          this.getText("LT00000576", "Subsidiary")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('customerCode'); } },
                          m(SortIconComponent, { column: 'customerCode' }),
                          this.getText("LT00008046", "Customer")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('mapType'); } },
                          m(SortIconComponent, { column: 'mapType' }),
                          this.getText("LT00001011", "Map Type")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('mapKey'); } },
                          m(SortIconComponent, { column: 'mapKey' }),
                          this.getText("LT00001014", "I/F Model")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('mapAttribute1'); } },
                          m(SortIconComponent, { column: 'mapAttribute1' }),
                          this.getText("LT00001013", "Samsung Model")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('createDate'); } },
                          m(SortIconComponent, { column: 'createDate' }),
                          this.getText("LT00000885", "Created On")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('createUID'); } },
                          m(SortIconComponent, { column: 'createUID' }),
                          this.getText("LT00000273", "Created By")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('updateUID'); } },
                          m(SortIconComponent, { column: 'updateUID' }),
                          this.getText("LT00000274", "Updated By")),

                        m("th.cursor-pointer",
                          { onclick: () => { onSortClick('updateDate'); } },
                          m(SortIconComponent, { column: 'updateDate' }),
                          this.getText("LT00000887", "Updated On")),

                        m("th", this.getText("LT00000092", "Action"))
                      )
                    ),
                    m("tbody",
                      modelMappings.length == 0 &&
                      m("tr.align-middle",
                        m("td.text-center",
                          { colspan: "21" },
                          languageModel.getText(
                            "EM00000141",
                            "No records found."
                          )
                        )
                      ),
                      modelMappings.length != 0 && modelMappings.map((data, index) =>
                        m(`tr.align-middle`,
                          m("td", m("span.cursor-text", data.subsidiaryCode)),
                          m("td", m("span.cursor-text", data.customerCode)),
                          m("td", m("span.cursor-text", data.mapType)),
                          m("td", m("span.cursor-text", data.mapKey)),
                          m("td", m("span.cursor-text", data.mapAttribute1)),
                          m("td", m("span.cursor-text", data.createDate)),
                          m("td", m("span.cursor-text", data.createUID)),
                          m("td", m("span.cursor-text", data.updateUID)),
                          m("td", m("span.cursor-text", data.updateDate)),
                          m('td',
                            m('i.bi.bi-pencil-square.list-action-icon[title="Edit"]',
                              {
                                onclick: () => { onUpdateAction(data); }
                              }),
                            m('i.bi.bi-trash-fill.list-action-icon[title="Delete"]',
                              {
                                onclick: () => { onDeleteAction(data); }
                              })
                          )
                        )
                      )
                    )
                  )
                )
              ),
              m(".col-12",
                m(paginationComponent, {
                  currentPage: pagination.pageIndex,
                  pageSize: pagination.pageSize,
                  totalItems: pagination.totalItems,
                  onPageChanged: onPageChanged,
                  onPageSizeChanged: onPageSizeChanged,
                })
              )
            )
          )
        ),

        // /** CREATE DIALOG */
        isModalManageOpen
        && m(ModelMappingAddAndEditDialog, {
          isOpen: isModalManageOpen,
          action: manageAction,
          data: selectedModelMapping ?? structuredClone(DEFAULT_ITEM),
          closeDialog: (reload) => {
            if (reload) {
              searchModelMapping();
            }

            isModalManageOpen = false;
            selectedModelMapping = null;
          }
        }),

        // /** EXCEL UPLOAD */
        isExcelUploadOpen
        && m(ModelMappingExcelUploadDialog,
          {
            filters,
            isOpen: isExcelUploadOpen,
            onClose: (reload) => {
              if (reload) {
                searchModelMapping();
              }
              isExcelUploadOpen = false;
            }
          }
        ),
        this.getBaseViews()
      ]
    }
  }
}
